import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { memo, useEffect, useMemo, useState } from 'react';
import {
  useAllTenantCompaniesQuery,
  useAllTenantFleetsQuery,
  useCreateRunProfileMutation,
  useDeleteRunProfileMutation,
  useRunProfilesQuery,
  useUpdateRunProfileMutation
} from '../../services/routing/queries';
import { MYLE_FLEET, NO_FLEET_ID, namedFleet, namesById } from '../../utils/format';
import type { FleetInRoutes } from '../../services/routing/api';
import type { RunProfile } from '../../types/routing';

const NEW = 'new';

/**
 * The no-fleet bucket. A route carrying no fleet is a Myle driver's own, and the run filters
 * already address it as fleet 0 — but nobody registers a name for "no fleet", so a picker built
 * from the tenant's fleet registry alone cannot select the one bucket every tenant has.
 */
const NO_FLEET: FleetInRoutes = { id: NO_FLEET_ID, name: MYLE_FLEET, named: false };

const withNoFleet = (registered: FleetInRoutes[]) =>
  registered.some(fleet => fleet.id === NO_FLEET.id) ? registered : [NO_FLEET, ...registered];

const label = (entry: { id: number; name: string }) => `${entry.name} (${entry.id})`;

/**
 * A tenant's saved run selections. One is loaded on the run screen instead of picking the same
 * fleets again, and the nightly schedule names one instead of holding its own copy — which is why
 * the fleet order lives here rather than on either of those screens: edit it once, and both the
 * next manual run and tonight's scheduled one plan the new order.
 */
const Panel = ({ tenant }: { tenant: string }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError, error } = useRunProfilesQuery(tenant);
  const create = useCreateRunProfileMutation(tenant);
  const update = useUpdateRunProfileMutation(tenant);
  const remove = useDeleteRunProfileMutation(tenant);

  const fleetData = useAllTenantFleetsQuery(tenant).data;
  const companyData = useAllTenantCompaniesQuery(tenant).data;
  const fleets = useMemo(() => withNoFleet(fleetData ?? []), [fleetData]);
  const companyOptions = useMemo(() => companyData ?? [], [companyData]);
  const profiles = useMemo(() => data?.profiles ?? [], [data]);

  // A stored selection is ids; the boxes show what those ids are called, or fleet 0 reads as a
  // bare "0" on the one screen that exists to say which fleets are meant. An id the registry no
  // longer has still reads as a fleet rather than as a loose number.
  const fleetNames = namesById(fleets);
  const renderFleets = (selected: string[]) =>
    selected.map(id => namedFleet(Number(id), fleetNames)).join(', ');

  const [picked, setPicked] = useState<string>(NEW);
  const [name, setName] = useState('');
  const [scope, setScope] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[][]>([]);
  const [companies, setCompanies] = useState<string[]>([]);

  const blank = () => {
    setPicked(NEW);
    setName('');
    setScope([]);
    setGroups([]);
    setCompanies([]);
  };

  // Cleared the moment the tenant changes, so one tenant's selection can never be saved onto
  // another — the ids in it mean different fleets there.
  useEffect(() => {
    blank();
  }, [tenant]);

  const load = (profile: RunProfile) => {
    setPicked(String(profile.profile_id));
    setName(profile.name);
    setScope(profile.fleet_ids.map(String));
    setGroups(profile.fleet_groups.map(group => group.map(String)));
    setCompanies(profile.company_ids.map(String));
  };

  const onPick = (value: string) => {
    if (value === NEW) {
      blank();
      return;
    }
    const profile = profiles.find(candidate => String(candidate.profile_id) === value);
    if (profile) load(profile);
  };

  // A fleet belongs to one group at a time — taking it into this one removes it from the others.
  const setGroup = (index: number, selected: string[]) =>
    setGroups(previous =>
      previous.map((group, at) =>
        at === index ? selected : group.filter(id => !selected.includes(id))
      )
    );

  // The API refuses a group that orders a fleet the scope excludes, so narrowing the scope drops
  // those entries here rather than letting Save fail with a 422 the operator has to decode.
  const onScopeChange = (selected: string[]) => {
    setScope(selected);
    if (selected.length) {
      setGroups(previous => previous.map(group => group.filter(id => selected.includes(id))));
    }
  };

  const move = (index: number, by: number) =>
    setGroups(previous => {
      const next = [...previous];
      const target = index + by;
      if (target < 0 || target >= next.length) return previous;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const removeGroup = (index: number) =>
    setGroups(previous => previous.filter((_, at) => at !== index));

  const filled = groups.map(group => group.filter(Boolean)).filter(group => group.length);
  // The same rule the API enforces, said before the request rather than after it: a profile that
  // selects nothing would be a selection covering the whole tenant.
  const selectsSomething = !!scope.length || !!filled.length || !!companies.length;
  const saveable = !!name.trim() && selectsSomething && !isError;

  const onSave = () => {
    if (!saveable) return;
    const body = {
      name: name.trim(),
      fleet_ids: scope.map(Number),
      fleet_groups: filled.map(group => group.map(Number)),
      company_ids: companies.map(Number)
    };
    const onError = (failure: unknown) =>
      enqueueSnackbar((failure as Error).message, { variant: 'error' });
    if (picked === NEW) {
      create.mutate(body, {
        onSuccess: profile => {
          load(profile);
          enqueueSnackbar(`Profile “${profile.name}” saved`);
        },
        onError
      });
      return;
    }
    update.mutate(
      { id: Number(picked), body },
      {
        onSuccess: profile => enqueueSnackbar(`Profile “${profile.name}” saved`),
        onError
      }
    );
  };

  const onDelete = () => {
    if (picked === NEW) return;
    remove.mutate(Number(picked), {
      onSuccess: () => {
        blank();
        enqueueSnackbar('Profile deleted');
      },
      // The API refuses to delete the profile the nightly run plans, and says so — that message
      // is the whole point of showing it.
      onError: failure => enqueueSnackbar((failure as Error).message, { variant: 'error' })
    });
  };

  const saving = create.isPending || update.isPending;

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        RUN PROFILES
      </Typography>
      {isError ? (
        <Alert severity="error">
          Could not read the run profiles for &ldquo;{tenant}&rdquo; ({(error as Error).message}).
          Saving is disabled until they load — otherwise this form would write a new profile over
          a name that already exists.
        </Alert>
      ) : isLoading ? (
        <Typography variant="body2" color="text.secondary">
          loading&#8230;
        </Typography>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            A saved selection: which fleets are planned, in what order, and whose rides they may
            take. Load one on the run screen, or point the nightly schedule at it — the schedule
            reads it when it fires, so editing a profile changes what tonight plans.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
            <TextField
              select
              label="Profile"
              value={picked}
              onChange={event => onPick(event.target.value)}
              helperText={`${profiles.length} saved`}
              SelectProps={{ SelectDisplayProps: { 'aria-label': 'profile' } }}
              sx={{ minWidth: { xs: 0, sm: 260 } }}
            >
              <MenuItem value={NEW}>New profile&#8230;</MenuItem>
              {profiles.map(profile => (
                <MenuItem key={profile.profile_id} value={String(profile.profile_id)}>
                  {profile.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Name"
              value={name}
              onChange={event => setName(event.target.value)}
              error={!name.trim()}
              helperText={name.trim() ? ' ' : 'a name is what the run and the schedule pick it by'}
              inputProps={{ maxLength: 191, 'aria-label': 'profile name' }}
              sx={{ minWidth: { xs: 0, sm: 260 } }}
            />
          </Stack>

          <TextField
            select
            label="Fleets"
            value={scope}
            onChange={event => onScopeChange(event.target.value as unknown as string[])}
            helperText="the scope — leave empty to plan every fleet"
            SelectProps={{
              multiple: true,
              renderValue: selected => renderFleets(selected as string[]),
              SelectDisplayProps: { 'aria-label': 'profile fleets' }
            }}
            sx={{ maxWidth: 420 }}
          >
            {fleets.map(fleet => (
              <MenuItem key={fleet.id} value={String(fleet.id)}>
                {label(fleet)}
              </MenuItem>
            ))}
          </TextField>

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Fleet groups, in planning order — the first group claims its rides before the next
              one is offered any. A fleet belongs to one group at a time, so taking it into a
              group moves it out of the one that had it. A fleet in no group is still planned,
              after every group.
            </Typography>
            {groups.map((group, index) => (
              <Stack key={index} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip label={index + 1} size="small" />
                <TextField
                  select
                  label={`Group ${index + 1} fleets`}
                  value={group}
                  onChange={event => setGroup(index, event.target.value as unknown as string[])}
                  SelectProps={{
                    multiple: true,
                    renderValue: selected => renderFleets(selected as string[]),
                    SelectDisplayProps: { 'aria-label': `group ${index + 1} fleets` }
                  }}
                  // On a phone the select takes a line of its own, under the number and the
                  // buttons that move it.
                  sx={{ minWidth: { xs: 0, sm: 320 }, flexGrow: 1, order: { xs: 1, sm: 0 } }}
                >
                  {(scope.length
                    ? fleets.filter(fleet => scope.includes(String(fleet.id)))
                    : fleets
                  ).map(fleet => (
                    <MenuItem key={fleet.id} value={String(fleet.id)}>
                      {label(fleet)}
                    </MenuItem>
                  ))}
                </TextField>
                <Stack direction="row" sx={{ ml: { xs: 'auto', sm: 0 } }}>
                  <IconButton
                    aria-label={`move group ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label={`move group ${index + 1} down`}
                    disabled={index === groups.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label={`remove group ${index + 1}`}
                    onClick={() => removeGroup(index)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            ))}
            <Stack direction="row">
              <Button size="small" onClick={() => setGroups(previous => [...previous, []])}>
                Add group
              </Button>
            </Stack>
          </Stack>

          <TextField
            select
            label="Companies"
            value={companies}
            onChange={event => setCompanies(event.target.value as unknown as string[])}
            helperText="leave empty to plan every company's rides"
            SelectProps={{
              multiple: true,
              renderValue: selected => (selected as string[]).join(', '),
              SelectDisplayProps: { 'aria-label': 'profile companies' }
            }}
            sx={{ maxWidth: 420 }}
          >
            {companyOptions.map(company => (
              <MenuItem key={company.id} value={String(company.id)}>
                {label(company)}
              </MenuItem>
            ))}
          </TextField>

          {!selectsSomething && (
            <Alert severity="info" sx={{ py: 0 }}>
              Pick a fleet, a fleet group or a company — a profile that selects nothing would plan
              the whole tenant.
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={onSave} disabled={saving || !saveable}>
              {picked === NEW ? 'Create profile' : 'Save profile'}
            </Button>
            <Button
              color="error"
              onClick={onDelete}
              disabled={picked === NEW || remove.isPending}
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

/**
 * Memoised on `tenant`, its only input — the same reason NightlySchedulePanel is: it sits beside
 * the parameter and engine forms, whose state changes would otherwise rebuild a few hundred fleet
 * and company menu items on every keystroke typed into them.
 */
export const RunProfilesPanel = memo(Panel);

export default RunProfilesPanel;
